import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../utils/apiClient';
import eventEmitter from '../utils/eventEmitter';
import { getLocalDateString, parseLocalDate } from '../utils/dateUtils';

const AvailabilityView = () => {
  const [calendarData, setCalendarData] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefreshing, setAutoRefreshing] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedDateEmployees, setSelectedDateEmployees] = useState([]);
  
  // Use ref to get current date value in event handler
  const currentDateRef = useRef(currentDate);
  
  // Update ref when currentDate changes
  useEffect(() => {
    currentDateRef.current = currentDate;
  }, [currentDate]);

  const fetchCalendarAvailability = useCallback(async (showRefreshIndicator = false) => {
    try {
      console.log('AvailabilityView: fetchCalendarAvailability called, showRefreshIndicator:', showRefreshIndicator);
      
      if (showRefreshIndicator) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const year = currentDateRef.current.getFullYear();
      const month = currentDateRef.current.getMonth() + 1;
      
      console.log('AvailabilityView: Fetching calendar data for', year, month);
              const response = await apiClient.get(`/api/calendar-availability?year=${year}&month=${month}`);
      const data = response.data;
      console.log('AvailabilityView: Received calendar data:', data.length, 'items');
      
      // Process data into calendar format
      const calendarMap = {};
      
      data.forEach(item => {
        // Handle both MySQL date format and ISO strings consistently
        let dateKey;
        if (typeof item.request_date === 'string') {
          // If it's already a string, use the date part (handle both "YYYY-MM-DD" and "YYYY-MM-DDTHH:MM:SS" formats)
          dateKey = item.request_date.split('T')[0];
        } else {
          // If it's a Date object, convert using local date string
          dateKey = getLocalDateString(item.request_date);
        }
        
        if (!calendarMap[dateKey]) {
          calendarMap[dateKey] = {
            total: 0,
            ranks: {
              'Captain': 0,
              'Lieutenant': 0,
              'Sergeant': 0,
              'Lifeguard': 0,
              'Rookie': 0
            },
            emts: 0,
            advanced: 0
          };
        }
        
        calendarMap[dateKey].total += item.available_count;
        calendarMap[dateKey].ranks[item.rank] += item.available_count;
        
        if (item.emt_training) {
          calendarMap[dateKey].emts += item.available_count;
        }
      });
      
      console.log('AvailabilityView: Processed calendar data:', Object.keys(calendarMap).length, 'days with data');
      setCalendarData(calendarMap);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('AvailabilityView: Failed to fetch calendar availability:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentDate]);

  useEffect(() => {
    fetchCalendarAvailability();
  }, [fetchCalendarAvailability, currentDate]);

  // Add visibility change listener to refresh when user returns to tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('AvailabilityView: Page became visible, refreshing data...');
        // Refresh data when tab becomes visible (user might have updated schedule in another tab)
        fetchCalendarAvailability(true);
      }
    };

    const handleFocus = () => {
      console.log('AvailabilityView: Window gained focus, refreshing data...');
      fetchCalendarAvailability(true);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchCalendarAvailability]);

  // Add periodic refresh every 30 seconds when component is active
  useEffect(() => {
    let intervalId;
    
    if (!document.hidden) {
      console.log('AvailabilityView: Setting up periodic refresh every 30 seconds...');
      intervalId = setInterval(() => {
        console.log('AvailabilityView: Periodic refresh triggered');
        fetchCalendarAvailability(true);
      }, 30000); // 30 seconds
    }

    return () => {
      if (intervalId) {
        console.log('AvailabilityView: Clearing periodic refresh interval');
        clearInterval(intervalId);
      }
    };
  }, [fetchCalendarAvailability]);

  // Add event listener for schedule updates
  useEffect(() => {
    console.log('AvailabilityView: Setting up scheduleUpdated event listener');
    
    const handleScheduleUpdate = async (data) => {
      console.log('AvailabilityView: Received scheduleUpdated event:', data);
      console.log('AvailabilityView: Current calendar data before update:', Object.keys(calendarData).length, 'days');
      setAutoRefreshing(true);
      
      // Add a small delay to ensure database transaction is committed
      console.log('AvailabilityView: Waiting for database transaction to complete...');
      await new Promise(resolve => setTimeout(resolve, 500)); // 500ms delay
      
      // Use the existing fetchCalendarAvailability function instead of duplicating logic
      await fetchCalendarAvailability(true);
      
      console.log('AvailabilityView: Calendar data refresh completed');
      
      // Hide auto-refresh notification after a short delay
      setTimeout(() => {
        setAutoRefreshing(false);
        console.log('AvailabilityView: Auto-refresh notification hidden');
      }, 2000);
    };

    eventEmitter.on('scheduleUpdated', handleScheduleUpdate);
    console.log('AvailabilityView: Event listener registered');
    
    return () => {
      console.log('AvailabilityView: Removing scheduleUpdated event listener');
      eventEmitter.off('scheduleUpdated', handleScheduleUpdate);
    };
  }, [fetchCalendarAvailability]); // FIXED: Added fetchCalendarAvailability as dependency

  const handleRefresh = () => {
    fetchCalendarAvailability(true);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const days = [];
    const startDate = new Date(firstDay);
    const startYear = startDate.getFullYear();
    const startMonth = startDate.getMonth();
    const startDay = startDate.getDate() - firstDay.getDay(); // Start from Sunday
    
    // Generate 42 days (6 weeks) to fill calendar grid
    for (let i = 0; i < 42; i++) {
      const currentDay = new Date(startYear, startMonth, startDay + i);
      days.push({
        date: currentDay,
        isCurrentMonth: currentDay.getMonth() === month,
        isToday: currentDay.toDateString() === new Date().toDateString(),
        dateKey: getLocalDateString(currentDay)
      });
    }
    
    return days;
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
    setSelectedDate(null);
  };

  const getMonthYearString = () => {
    return currentDate.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long' 
    });
  };

  const getDayData = (dateKey) => {
    return calendarData[dateKey] || {
      total: 0,
      ranks: {
        'Captain': 0,
        'Lieutenant': 0,
        'Sergeant': 0,
        'Lifeguard': 0,
        'Rookie': 0
      },
      emts: 0,
      advanced: 0
    };
  };

  const getDayClassName = (day) => {
    let className = 'calendar-day';
    
    if (!day.isCurrentMonth) {
      className += ' other-month';
    }
    
    if (day.isToday) {
      className += ' today';
    }
    
    if (selectedDate === day.dateKey) {
      className += ' selected';
    }
    
    const dayData = getDayData(day.dateKey);
    if (dayData.total > 0) {
      className += ' has-availability';
    }
    
    return className;
  };

  // Fetch employees for selected date
  const fetchEmployeesForDate = useCallback(async (date) => {
    try {
              const response = await apiClient.get(`/api/availability?date=${date}`);
      const availableEmployees = response.data.filter(emp => emp.is_available === 1 || emp.is_available === true);
      
      // Sort employees by rank priority and then by name
      availableEmployees.sort((a, b) => {
        const rankPriority = {
          'Captain': 5,
          'Lieutenant': 4, 
          'Sergeant': 3,
          'Lifeguard': 2,
          'Rookie': 1
        };
        
        const aPriority = rankPriority[a.rank] || 0;
        const bPriority = rankPriority[b.rank] || 0;
        
        if (aPriority !== bPriority) {
          return bPriority - aPriority; // Higher rank first
        }
        
        // Same rank, sort by name
        const aName = `${a.first_name || a.username} ${a.last_name || ''}`.trim();
        const bName = `${b.first_name || b.username} ${b.last_name || ''}`.trim();
        return aName.localeCompare(bName);
      });
      
      setSelectedDateEmployees(availableEmployees);
    } catch (error) {
      console.error('Failed to fetch employees for date:', error);
      setSelectedDateEmployees([]);
    }
  }, []);

  const handleDayClick = (day) => {
    if (day.isCurrentMonth) {
      const newSelectedDate = selectedDate === day.dateKey ? null : day.dateKey;
      setSelectedDate(newSelectedDate);
      
      if (newSelectedDate) {
        fetchEmployeesForDate(newSelectedDate);
      } else {
        setSelectedDateEmployees([]);
      }
    }
  };

  const days = getDaysInMonth(currentDate);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading calendar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Availability Calendar</h1>
        <p className="page-subtitle">Monthly view of guard availability by rank and training</p>
        {lastUpdated && (
          <div style={{ marginTop: '0.5rem', padding: '0.5rem', backgroundColor: '#f8f9fa', borderRadius: '4px', fontSize: '0.85rem', color: '#666' }}>
            📅 Last updated: {lastUpdated.toLocaleString()} | 
            📊 Data points: {Object.keys(calendarData).length} days | 

          </div>
        )}
      </div>

      {autoRefreshing && (
        <div className="alert alert-info">
          🔄 Calendar updating automatically due to schedule changes...
        </div>
      )}

      {refreshing && !autoRefreshing && (
        <div className="alert alert-info">
          🔄 Refreshing calendar data...
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div className="calendar-header">
            <button 
              onClick={() => navigateMonth(-1)}
              className="btn btn-secondary"
            >
              ← Previous
            </button>
            <h2 className="calendar-title">{getMonthYearString()}</h2>
            <div className="calendar-controls">
              <button 
                onClick={handleRefresh}
                className="btn btn-primary"
                disabled={refreshing}
                style={{ marginRight: '0.5rem' }}
              >
                {refreshing ? '🔄 Refreshing...' : '🔄 Refresh Now'}
              </button>
              <button 
                onClick={() => {
                  console.log('AvailabilityView: Force refresh triggered by user');
                  setRefreshing(true);
                  setTimeout(() => fetchCalendarAvailability(true), 100);
                }}
                className="btn btn-success"
                disabled={refreshing}
                style={{ marginRight: '0.5rem' }}
              >
                🔁 Force Refresh
              </button>
              <button 
                onClick={() => navigateMonth(1)}
                className="btn btn-secondary"
              >
                Next →
              </button>
            </div>
          </div>
        </div>

        <div className="calendar-container">
          <div className="calendar-grid">
            {/* Week day headers */}
            {weekDays.map(day => (
              <div key={day} className="calendar-weekday">
                {day}
              </div>
            ))}
            
            {/* Calendar days */}
            {days.map((day, index) => {
              const dayData = getDayData(day.dateKey);
              
              return (
                <div
                  key={index}
                  className={getDayClassName(day)}
                  onClick={() => handleDayClick(day)}
                >
                  <div className="calendar-day-number">
                    {day.date.getDate()}
                  </div>
                  
                  {day.isCurrentMonth && dayData.total > 0 && (
                    <div className="calendar-day-content">
                      <div className="availability-total">
                        <strong>{dayData.total} Total</strong>
                      </div>
                      
                      <div className="availability-ranks">
                        {Object.entries(dayData.ranks).map(([rank, count]) => (
                          count > 0 && (
                            <div key={rank} className={`rank-count rank-${rank.toLowerCase()}`}>
                              {rank.slice(0, 3)}: {count}
                            </div>
                          )
                        ))}
                      </div>
                      
                      {dayData.emts > 0 && (
                        <div className="emt-count">
                          EMT: {dayData.emts}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {day.isCurrentMonth && dayData.total === 0 && (
                    <div className="no-availability">
                      No availability
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Selected date details */}
      {selectedDate && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">
              Details for {parseLocalDate(selectedDate).toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </h2>
          </div>
          
          <div className="selected-day-details">
            {(() => {
              const dayData = getDayData(selectedDate);
              
              if (dayData.total === 0) {
                return (
                  <div className="alert alert-warning">
                    No guards available on this day
                  </div>
                );
              }
              
              return (
                <div className="availability-breakdown">
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Total Available</label>
                      <p className="availability-number total-count">{dayData.total}</p>
                    </div>
                    <div className="form-group">
                      <label className="form-label">EMT Trained</label>
                      <p className="availability-number emt-count">{dayData.emts}</p>
                    </div>
                  </div>
                  
                  <div className="rank-breakdown">
                    <h3 className="breakdown-title">By Rank</h3>
                    <div className="form-row">
                      {Object.entries(dayData.ranks).map(([rank, count]) => (
                        <div key={rank} className="form-group">
                          <label className="form-label">{rank}s</label>
                          <p className={`availability-number rank-${rank.toLowerCase()}`}>
                            {count}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Employee List */}
                  {selectedDateEmployees.length > 0 && (
                    <div className="employee-list-section">
                      <h3 className="breakdown-title">Available Employees</h3>
                      <div className="employees-grid">
                        {selectedDateEmployees.map((employee) => (
                          <div key={employee.id} className="employee-availability-card">
                            <div className="employee-header">
                              <div className="employee-name">
                                {employee.first_name && employee.last_name 
                                  ? `${employee.first_name} ${employee.last_name}`
                                  : employee.username}
                              </div>
                              <span className={`rank-badge rank-${employee.rank.toLowerCase()}`}>
                                {employee.rank}
                              </span>
                            </div>
                            <div className="employee-details">
                              {employee.beach && (
                                <div className="employee-detail">
                                  <span className="detail-label">Beach:</span>
                                  <span className="detail-value">{employee.beach}</span>
                                </div>
                              )}
                              <div className="employee-detail">
                                <span className="detail-label">Experience:</span>
                                <span className="detail-value">{employee.years_of_service || 0} years</span>
                              </div>
                              <div className="employee-badges">
                                {(employee.emt_training === 1 || employee.emt_training === true) && (
                                  <span className="training-badge emt-badge">EMT</span>
                                )}
                                {(employee.advanced_training === 1 || employee.advanced_training === true) && (
                                  <span className="training-badge advanced-badge">Advanced</span>
                                )}
                                {(employee.is_rookie === 1 || employee.is_rookie === true) && (
                                  <span className="training-badge rookie-badge">Rookie</span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Legend</h2>
        </div>
        
        <div className="calendar-legend">
          <div className="legend-item">
            <div className="legend-color has-availability"></div>
            <span>Days with availability</span>
          </div>
          <div className="legend-item">
            <div className="legend-color today"></div>
            <span>Today</span>
          </div>
          <div className="legend-item">
            <div className="legend-color selected"></div>
            <span>Selected day</span>
          </div>
        </div>
        
        <div className="rank-legend">
          <h3>Rank Colors:</h3>
          <div className="rank-colors">
            <span className="rank-badge rank-captain">Captain</span>
            <span className="rank-badge rank-lieutenant">Lieutenant</span>
            <span className="rank-badge rank-sergeant">Sergeant</span>
            <span className="rank-badge rank-lifeguard">Lifeguard</span>
            <span className="rank-badge rank-rookie">Rookie</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityView; 